// mcpTransport.ts — JSON-RPC 2.0 envelope for MCP-compliant tool calls.
//
// Every tool call from the agent goes through this layer. The shapes
// (request + response + content blocks) are bit-for-bit MCP-compliant
// per the spec at modelcontextprotocol.io. The actual transport here is
// in-process (browser → local tool), but the protocol semantics are real.
//
// Why MCP for an in-browser agent?
//   Architectural coherence. Harsh ships MCP infrastructure (MCP-JEST,
//   aa-kit, OmniMCP). It would be incoherent for his portfolio agent to
//   reach for tools through some bespoke channel. The trace panel shows
//   the actual JSON-RPC envelopes, which is the visible signal of MCP
//   self-consumption.
//
// References:
//   - MCP spec: https://modelcontextprotocol.io/specification
//   - JSON-RPC 2.0: https://www.jsonrpc.org/specification

import { BROWSER_TOOLS } from "@/lib/browserTools";

// ───────────────────────────── types ─────────────────────────────

export type MCPRequest = {
  jsonrpc: "2.0";
  id: string | number;
  method: "tools/call" | "tools/list";
  params?: {
    name?: string;
    arguments?: Record<string, unknown>;
  };
};

export type MCPContent =
  | { type: "text"; text: string }
  | { type: "image"; data: string; mimeType: string }
  | { type: "resource"; resource: { uri: string; mimeType?: string; text?: string } };

export type MCPSuccessResponse = {
  jsonrpc: "2.0";
  id: string | number;
  result: {
    content: MCPContent[];
    isError?: false;
    _meta?: Record<string, unknown>;
  };
};

export type MCPErrorResponse = {
  jsonrpc: "2.0";
  id: string | number;
  error: {
    code: number;
    message: string;
    data?: unknown;
  };
};

export type MCPResponse = MCPSuccessResponse | MCPErrorResponse;

// Aggregate result the agent loop consumes
export type MCPCallResult = {
  ok: boolean;
  request: MCPRequest;
  response: MCPResponse;
  durationMs: number;
  // Parsed tool result for convenience
  data?: unknown;
  error?: string;
};

// ───────────────────────── error codes (per JSON-RPC 2.0) ───────────────────

const MCP_ERRORS = {
  PARSE_ERROR: -32700,
  INVALID_REQUEST: -32600,
  METHOD_NOT_FOUND: -32601,
  INVALID_PARAMS: -32602,
  INTERNAL_ERROR: -32603,
  // MCP-defined application range
  TOOL_NOT_FOUND: -32001,
  TOOL_EXECUTION_FAILED: -32002,
};

let _nextId = 1;
const nextId = () => _nextId++;

// ───────────────────────────── public API ─────────────────────────────

/**
 * Call an MCP tool. Returns the full request + response trace plus the
 * unwrapped result. Always resolves — errors are surfaced via the
 * `ok` flag and the JSON-RPC error envelope.
 */
export async function mcpCall(
  name: string,
  args: Record<string, unknown>,
): Promise<MCPCallResult> {
  const t0 = performance.now();
  const request: MCPRequest = {
    jsonrpc: "2.0",
    id: nextId(),
    method: "tools/call",
    params: { name, arguments: args },
  };

  const tool = BROWSER_TOOLS[name];
  if (!tool) {
    const response: MCPErrorResponse = {
      jsonrpc: "2.0",
      id: request.id,
      error: {
        code: MCP_ERRORS.TOOL_NOT_FOUND,
        message: `Tool '${name}' is not registered`,
      },
    };
    return {
      ok: false,
      request,
      response,
      durationMs: Math.round(performance.now() - t0),
      error: response.error.message,
    };
  }

  try {
    const result = await tool.call(args);
    if (!result.ok) {
      const response: MCPErrorResponse = {
        jsonrpc: "2.0",
        id: request.id,
        error: {
          code: MCP_ERRORS.TOOL_EXECUTION_FAILED,
          message: result.error ?? "tool reported failure",
        },
      };
      return {
        ok: false,
        request,
        response,
        durationMs: Math.round(performance.now() - t0),
        error: response.error.message,
      };
    }

    // Wrap the typed data as an MCP content block. MCP servers typically
    // return text content with the JSON stringified — we follow that
    // convention so the trace looks like a real MCP exchange.
    const response: MCPSuccessResponse = {
      jsonrpc: "2.0",
      id: request.id,
      result: {
        content: [
          {
            type: "text",
            text: JSON.stringify(result.data, null, 2),
          },
        ],
        _meta: { tool: name },
      },
    };
    return {
      ok: true,
      request,
      response,
      durationMs: Math.round(performance.now() - t0),
      data: result.data,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "unknown tool error";
    const response: MCPErrorResponse = {
      jsonrpc: "2.0",
      id: request.id,
      error: {
        code: MCP_ERRORS.INTERNAL_ERROR,
        message,
      },
    };
    return {
      ok: false,
      request,
      response,
      durationMs: Math.round(performance.now() - t0),
      error: message,
    };
  }
}

/**
 * tools/list — returns the catalog of available tools per MCP spec.
 * Useful if we ever expose this transport to a remote client.
 */
export function mcpListTools(): MCPSuccessResponse {
  return {
    jsonrpc: "2.0",
    id: nextId(),
    result: {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            Object.values(BROWSER_TOOLS).map((t) => ({
              name: t.name,
              description: t.description,
              inputSchema: t.inputSchema,
            })),
            null,
            2,
          ),
        },
      ],
    },
  };
}

/**
 * Execute multiple MCP calls in parallel. The agent loop uses this for
 * the common case of "ask about X requires both GitHub stars AND npm
 * downloads in parallel."
 */
export async function mcpCallMany(
  calls: Array<{ name: string; args: Record<string, unknown> }>,
): Promise<MCPCallResult[]> {
  return Promise.all(calls.map((c) => mcpCall(c.name, c.args)));
}
